import React, { useState } from 'react'
import { 
  Heart, 
  Coffee, 
  Star, 
  Zap, 
  Crown,
  Gift,
  DollarSign,
  MessageSquare,
  Send,
  CheckCircle
} from 'lucide-react'

interface TipCreatorProps {
  creatorAddress: string
  creatorName: string
  onTip: (amount: number, message?: string) => void
  isLoading?: boolean
  recentTips?: Array<{
    amount: number
    currency: string
    message?: string
    timestamp: Date
    from: string
  }>
}

const quickTipAmounts = [5, 10, 25, 50, 100]

const tipMessages = [
  "Thanks for the amazing content! 💜",
  "Keep up the great work! 🔥",
  "This really helped me! 🙏",
  "You're awesome! ⭐",
  "Much love from a fan! ❤️"
]

export default function TipCreator({ 
  creatorAddress, 
  creatorName,
  onTip, 
  isLoading = false,
  recentTips = []
}: TipCreatorProps) {
  const [selectedAmount, setSelectedAmount] = useState<number | null>(null)
  const [customAmount, setCustomAmount] = useState<string>('')
  const [message, setMessage] = useState<string>('')
  const [showSuccess, setShowSuccess] = useState(false)

  const handleTip = (amount: number) => {
    setSelectedAmount(amount)
    onTip(amount, message || undefined)
    
    // Show success animation
    setShowSuccess(true)
    setTimeout(() => {
      setShowSuccess(false)
      setSelectedAmount(null)
      setMessage('')
    }, 3000)
  }

  const handleCustomTip = () => {
    const amount = parseFloat(customAmount)
    if (amount > 0) {
      handleTip(amount)
    }
  }

  const formatCurrency = (amount: number) => `${amount} SUI`

  if (showSuccess) {
    return (
      <div className="max-w-md mx-auto bg-white rounded-2xl shadow-lg p-8 text-center">
        <div className="mb-6">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h3 className="text-2xl font-bold text-gray-900 mb-2">Tip Sent!</h3>
          <p className="text-gray-600">
            Thank you for supporting {creatorName}! Your tip has been sent.
          </p>
        </div>
        <div className="bg-green-50 rounded-lg p-4">
          <div className="text-lg font-semibold text-green-800">
            {formatCurrency(selectedAmount || 0)} sent successfully
          </div>
          <div className="text-sm text-green-600 mt-1">
            90% goes directly to {creatorName}
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-8">
        <h2 className="text-3xl font-bold text-gray-900 mb-2">
          Tip {creatorName}
        </h2>
        <p className="text-gray-600">
          Show your appreciation with a tip. 90% goes directly to the creator!
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Tip Interface */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Send a Tip</h3>
          
          {/* Quick Amount Buttons */}
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-3">
              Choose amount (SUI)
            </label>
            <div className="grid grid-cols-3 gap-3 mb-4">
              {quickTipAmounts.map((amount) => (
                <button
                  key={amount}
                  onClick={() => setSelectedAmount(amount)}
                  className={`p-3 rounded-lg border-2 transition-all duration-200 ${
                    selectedAmount === amount
                      ? 'border-purple-500 bg-purple-50 text-purple-700'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-center">
                    {amount <= 10 && <Coffee className="w-4 h-4 mr-1" />}
                    {amount > 10 && amount <= 25 && <Heart className="w-4 h-4 mr-1" />}
                    {amount > 25 && amount <= 50 && <Star className="w-4 h-4 mr-1" />}
                    {amount > 50 && <Crown className="w-4 h-4 mr-1" />}
                    <span className="font-semibold">{amount}</span>
                  </div>
                </button>
              ))}
            </div>

            {/* Custom Amount */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Custom amount
              </label>
              <div className="flex">
                <input
                  type="number"
                  min="0.1"
                  step="0.1"
                  placeholder="Enter amount"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="flex-1 px-3 py-2 border border-gray-300 rounded-l-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
                <span className="px-3 py-2 bg-gray-100 border border-l-0 border-gray-300 rounded-r-lg text-gray-600">
                  SUI
                </span>
              </div>
            </div>

            {/* Message */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Message (optional)
              </label>
              <div className="space-y-2">
                <select
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="">Select a message...</option>
                  {tipMessages.map((msg, index) => (
                    <option key={index} value={msg}>{msg}</option>
                  ))}
                </select>
                <textarea
                  placeholder="Or write your own message..."
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Send Tip Button */}
            <button
              onClick={() => {
                if (selectedAmount) {
                  handleTip(selectedAmount)
                } else if (customAmount) {
                  handleCustomTip()
                }
              }}
              disabled={isLoading || (!selectedAmount && !customAmount)}
              className="w-full bg-purple-600 hover:bg-purple-700 disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold py-3 px-6 rounded-lg transition-all duration-200 flex items-center justify-center"
            >
              {isLoading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Sending Tip...
                </>
              ) : (
                <>
                  <Send className="w-5 h-5 mr-2" />
                  Send {selectedAmount ? formatCurrency(selectedAmount) : customAmount ? formatCurrency(parseFloat(customAmount)) : 'Tip'}
                </>
              )}
            </button>
          </div>

          {/* Tip Info */}
          <div className="bg-purple-50 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <Gift className="w-5 h-5 text-purple-600 mr-2" />
              <span className="text-sm font-medium text-purple-800">Tip Benefits</span>
            </div>
            <ul className="text-sm text-purple-700 space-y-1">
              <li>• 90% goes directly to {creatorName}</li>
              <li>• Instant blockchain transaction</li>
              <li>• Creator gets notified immediately</li>
              <li>• Shows up in their earnings dashboard</li>
            </ul>
          </div>
        </div>

        {/* Recent Tips */}
        <div className="bg-white rounded-2xl shadow-lg p-6">
          <h3 className="text-xl font-semibold text-gray-900 mb-6">Recent Tips</h3>
          
          {recentTips.length > 0 ? (
            <div className="space-y-4">
              {recentTips.slice(0, 5).map((tip, index) => (
                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                      <Heart className="w-5 h-5 text-purple-600" />
                    </div>
                    <div>
                      <div className="font-medium text-gray-900">
                        {formatCurrency(tip.amount)}
                      </div>
                      {tip.message && (
                        <div className="text-sm text-gray-600 truncate max-w-32">
                          "{tip.message}"
                        </div>
                      )}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-sm text-gray-500">
                      {tip.from.slice(0, 6)}...{tip.from.slice(-4)}
                    </div>
                    <div className="text-xs text-gray-400">
                      {tip.timestamp.toLocaleDateString()}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8">
              <Heart className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">No tips yet. Be the first to support this creator!</p>
            </div>
          )}

          {/* Tip Stats */}
          <div className="mt-6 pt-6 border-t border-gray-200">
            <div className="grid grid-cols-2 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {recentTips.length}
                </div>
                <div className="text-sm text-gray-600">Total Tips</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">
                  {recentTips.reduce((sum, tip) => sum + tip.amount, 0)}
                </div>
                <div className="text-sm text-gray-600">SUI Tipped</div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
